import { ExtendedComposition } from '@seen/core/models/compositions/composition'
import { ExtendedEncounter } from '@seen/core/models/encounters/encounter'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIsRenderSafe } from '#src/contexts/RenderSafeContext.tsx'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'

export function useEncounterDiagnoses(
  compositionReferenceString: string | null | undefined
) {
  /* Inside an AI suggestion's widget, linking a condition is a clinical
   * Encounter.diagnosis write standing on content the clinician has not
   * accepted. The gate lives HERE rather than at the call sites because this
   * hook is the choke point all four of them share (the mention picker, both
   * insert-current-conditions buttons, and the conditions-addressed block), so
   * a call site added later is gated by construction. The call sites still
   * insert their chip; only the encounter patch is suppressed, and the accept
   * pipeline's applyConditionMentionAccept performs the real link on accept —
   * recorded in the receipt, so an undo compensates it. */
  const renderSafe = useIsRenderSafe()

  const { data: composition, isLoading: isLoadingComposition } = useRead(
    ExtendedComposition,
    compositionReferenceString
  )

  const encounterReferenceString = composition?.encounter?.reference

  const { data: encounter, isLoading: isLoadingEncounter } = useRead(
    ExtendedEncounter,
    encounterReferenceString
  )

  const { mutate: patchEncounter } = usePatchMutation<ExtendedEncounter>()

  /* Server-derived condition reference strings from encounter.diagnosis[] */
  const serverConditionRefStrings = useMemo(
    () =>
      (encounter?.diagnosis ?? [])
        .map((d) => d.condition?.reference)
        .filter((ref): ref is string => !!ref),
    [encounter]
  )

  /* Optimistic state: null means "use server state" */
  const [optimisticRefStrings, setOptimisticRefStrings] = useState<
    string[] | null
  >(null)

  /* Clean up optimistic state when server catches up */
  useEffect(() => {
    if (optimisticRefStrings === null) {
      return
    }
    /* Check if arrays match exactly (same elements in same order) */
    if (
      optimisticRefStrings.length === serverConditionRefStrings.length &&
      optimisticRefStrings.every(
        (ref, i) => ref === serverConditionRefStrings[i]
      )
    ) {
      setOptimisticRefStrings(null)
    }
  }, [serverConditionRefStrings, optimisticRefStrings])

  const conditionReferenceStrings =
    optimisticRefStrings ?? serverConditionRefStrings

  const addConditions = useCallback(
    (refStrings: string[]) => {
      if (renderSafe || !encounter || refStrings.length === 0) {
        return
      }
      const current = optimisticRefStrings ?? serverConditionRefStrings
      const newRefs = refStrings.filter((r) => !current.includes(r))
      if (newRefs.length === 0) {
        return
      }
      const next = [...current, ...newRefs]
      setOptimisticRefStrings(next)

      patchEncounter(
        {
          instance: encounter,
          mutateFn: (enc) => {
            const existingRefs = new Set(
              enc.diagnosis.map((d) => d.condition?.reference)
            )
            const toAdd = newRefs.filter((r) => !existingRefs.has(r))
            enc.diagnosis = [
              ...enc.diagnosis,
              ...toAdd.map((ref) => ({ condition: { reference: ref } })),
            ]
          },
        },
        {
          onError: () => {
            setOptimisticRefStrings(null)
          },
        }
      )
    },
    [
      renderSafe,
      encounter,
      optimisticRefStrings,
      serverConditionRefStrings,
      patchEncounter,
    ]
  )

  const removeCondition = useCallback(
    (refString: string) => {
      if (renderSafe || !encounter) {
        return
      }
      const current = optimisticRefStrings ?? serverConditionRefStrings
      const next = current.filter((ref) => ref !== refString)
      setOptimisticRefStrings(next)

      patchEncounter(
        {
          instance: encounter,
          mutateFn: (enc) => {
            enc.diagnosis = enc.diagnosis.filter(
              (d) => d.condition?.reference !== refString
            )
          },
        },
        {
          onError: () => {
            setOptimisticRefStrings(null)
          },
        }
      )
    },
    [
      renderSafe,
      encounter,
      optimisticRefStrings,
      serverConditionRefStrings,
      patchEncounter,
    ]
  )

  const reorderConditions = useCallback(
    (orderedRefStrings: string[]) => {
      if (renderSafe || !encounter) {
        return
      }
      setOptimisticRefStrings(orderedRefStrings)

      patchEncounter(
        {
          instance: encounter,
          mutateFn: (enc) => {
            const diagnosisMap = new Map(
              enc.diagnosis.map((d) => [d.condition?.reference, d])
            )
            const reordered = orderedRefStrings.map(
              (ref) =>
                diagnosisMap.get(ref) ?? { condition: { reference: ref } }
            )
            const orphaned = enc.diagnosis.filter(
              (d) => !d.condition?.reference
            )
            enc.diagnosis = [...reordered, ...orphaned]
          },
        },
        {
          onError: () => {
            setOptimisticRefStrings(null)
          },
        }
      )
    },
    [renderSafe, encounter, patchEncounter]
  )

  return {
    conditionReferenceStrings,
    isLoading: isLoadingComposition || isLoadingEncounter,
    hasEncounter: !!encounterReferenceString,
    addConditions,
    removeCondition,
    reorderConditions,
  }
}
